/**
 * WalletAddressStep Component
 * ============================
 * Collects cryptocurrency wallet address with network selection.
 * Validates address format per-network before submission.
 */

import React, { useState } from 'react';
import { Wallet, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';

const NETWORKS = [
  { id: 'ethereum', label: 'Ethereum', prefix: '0x', icon: 'ETH' },
  { id: 'bitcoin', label: 'Bitcoin', prefix: '', icon: 'BTC' },
  { id: 'polygon', label: 'Polygon', prefix: '0x', icon: 'MATIC' },
  { id: 'bsc', label: 'BSC', prefix: '0x', icon: 'BNB' },
  { id: 'arbitrum', label: 'Arbitrum', prefix: '0x', icon: 'ARB' },
  { id: 'optimism', label: 'Optimism', prefix: '0x', icon: 'OP' },
  { id: 'solana', label: 'Solana', prefix: '', icon: 'SOL' },
];

function validateAddress(address, network) {
  if (!address || !address.trim()) return 'Wallet address is required';

  const trimmed = address.trim();

  // EVM networks (Ethereum, Polygon, BSC, Arbitrum, Optimism)
  if (['ethereum', 'polygon', 'bsc', 'arbitrum', 'optimism'].includes(network)) {
    if (!/^0x[a-fA-F0-9]{40}$/.test(trimmed)) {
      return 'Invalid EVM address. Must start with 0x followed by 40 hex characters.';
    }
    return null;
  }

  // Bitcoin
  if (network === 'bitcoin') {
    // Legacy (1...), P2SH (3...), Bech32 (bc1...)
    if (!/^(1|3)[a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(trimmed) &&
        !/^bc1[a-zA-HJ-NP-Z0-9]{25,62}$/.test(trimmed)) {
      return 'Invalid Bitcoin address.';
    }
    return null;
  }

  // Solana
  if (network === 'solana') {
    if (!/^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(trimmed)) {
      return 'Invalid Solana address.';
    }
    return null;
  }

  return null;
}

export default function WalletAddressStep({ onComplete, onBack, client, stepConfig }) {
  const supportedNetworks = stepConfig?.supported_networks || ['ethereum', 'bitcoin'];
  const availableNetworks = NETWORKS.filter(n => supportedNetworks.includes(n.id));

  const [network, setNetwork] = useState(availableNetworks[0]?.id || 'ethereum');
  const [address, setAddress] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    const validationError = validateAddress(address, network);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      await client.completeStep('wallet_address', {
        address: address.trim(),
        network,
      });
      onComplete({ address: address.trim(), network });
    } catch (err) {
      setError(err.message || 'Failed to submit wallet address');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.iconWrapper}>
        <Wallet size={32} style={{ color: 'var(--sdk-primary, #0D9488)' }} />
      </div>

      <h2 style={styles.title}>Wallet Address</h2>
      <p style={styles.subtitle}>
        Enter your cryptocurrency wallet address for compliance screening.
      </p>

      {/* Network Selector */}
      <div style={styles.fieldGroup}>
        <label style={styles.label}>Network</label>
        <div style={styles.networkGrid}>
          {availableNetworks.map(n => (
            <button
              key={n.id}
              type="button"
              onClick={() => { setNetwork(n.id); setError(null); }}
              style={{
                ...styles.networkButton,
                ...(network === n.id ? styles.networkButtonActive : {}),
              }}
            >
              <span style={styles.networkIcon}>{n.icon}</span>
              <span style={styles.networkLabel}>{n.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Address Input */}
      <div style={styles.fieldGroup}>
        <label style={styles.label}>Wallet Address</label>
        <input
          type="text"
          value={address}
          onChange={(e) => { setAddress(e.target.value); setError(null); }}
          placeholder={
            ['ethereum', 'polygon', 'bsc', 'arbitrum', 'optimism'].includes(network)
              ? '0x...'
              : network === 'bitcoin'
              ? '1... or 3... or bc1...'
              : 'Enter wallet address'
          }
          style={styles.input}
          autoComplete="off"
          spellCheck="false"
        />
      </div>

      {error && (
        <div style={styles.errorRow}>
          <AlertCircle size={14} color="#EF4444" />
          <span style={styles.errorText}>{error}</span>
        </div>
      )}

      {/* Navigation */}
      <div style={styles.buttonRow}>
        {onBack && (
          <button type="button" onClick={onBack} style={styles.backButton}>
            <ChevronLeft size={16} />
            Back
          </button>
        )}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting || !address.trim()}
          style={{
            ...styles.continueButton,
            opacity: submitting || !address.trim() ? 0.6 : 1,
          }}
        >
          {submitting ? 'Submitting...' : 'Continue'}
          {!submitting && <ChevronRight size={16} />}
        </button>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    padding: '32px 24px',
    minHeight: '400px',
  },
  iconWrapper: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    background: 'rgba(var(--sdk-primary-rgb, 13, 148, 136), 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px',
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1F2937',
    margin: '0 0 8px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#6B7280',
    margin: '0 0 24px',
    lineHeight: '1.5',
  },
  fieldGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '8px',
  },
  networkGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
    gap: '8px',
  },
  networkButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    padding: '12px 8px',
    border: '2px solid #E5E7EB',
    borderRadius: '10px',
    background: '#FFFFFF',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  networkButtonActive: {
    borderColor: 'var(--sdk-primary, #0D9488)',
    background: 'rgba(var(--sdk-primary-rgb, 13, 148, 136), 0.05)',
  },
  networkIcon: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#6B7280',
  },
  networkLabel: {
    fontSize: '12px',
    color: '#374151',
    fontWeight: '500',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    fontSize: '14px',
    fontFamily: 'monospace',
    border: '2px solid #E5E7EB',
    borderRadius: '10px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s ease',
  },
  errorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '16px',
  },
  errorText: {
    fontSize: '13px',
    color: '#EF4444',
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 'auto',
    paddingTop: '24px',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '10px 16px',
    fontSize: '14px',
    color: '#6B7280',
    background: 'transparent',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  continueButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#FFFFFF',
    background: 'var(--sdk-primary, #0D9488)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    marginLeft: 'auto',
  },
};
